import { Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable, of } from 'rxjs';
import { HttpService } from '../http/http.service';
import { IWebContant } from './liferay.interface';
import { getWebContentUrl } from '../../utils/api.util';
import { LIFERAY_CMS_LABEL } from '../../utils/liferay.constant';

interface ILiferayContent<T> {
  [key:string]: IWebContant<T>
}

@Injectable({
  providedIn: 'root'
})
export class LiferayService {
  private readonly liferayCMS = new BehaviorSubject<ILiferayContent<any>>({});

  constructor(private httpService: HttpService) { }

  /**
   * Get content from Liferay CMS by contentId
   * @param contentType
   * @param contentId
   * @returns Observable<IWebContant<T>>
   */
  getWebContent<T>(contentType: string, contentId: string): Observable<IWebContant<T>> {
    const cachedContent:IWebContant<T> = this.liferayCMS.getValue()[contentId];
    return cachedContent ? of<IWebContant<T>>(cachedContent) : this.getWebContentFromAPI<T>(contentType, contentId);
  }

  /**
   * Get content from API by contentId
   * @param contentType
   * @param contentId
   * @returns Observable<IWebContant<T>>
   */
  getWebContentFromAPI<T>(contentType: string, contentId: string): Observable<IWebContant<T>> {
    const url = getWebContentUrl(contentId);
    return this.httpService.get<T>(url).pipe(
      map((results: T) => {
        const formattedData: IWebContant<T> = { id: contentId, value: results, type: contentType };
        this.addLiferayContent<T>(contentType, contentId, formattedData);
        return formattedData;
      }),
    );
  }

  /**
   * Add or update content in Liferay CMS and sessionStorage
   * @param contentType
   * @param contentId
   * @param value
   */
  addLiferayContent<T>(contentType: string, contentId: string, value: IWebContant<T>): void {
    const liferayCMS = this.liferayCMS.getValue();
    liferayCMS[contentId] = value;
    sessionStorage.setItem(LIFERAY_CMS_LABEL, JSON.stringify(liferayCMS));
    this.liferayCMS.next(liferayCMS);
  }
}
